import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatPseudoCheckboxState,
  MatOption,
  MatOptgroup,
  MatPseudoCheckbox,
} from '@angular/material/core';
import {
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { FormlyFieldSelect } from '@ngx-formly/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import {
  NgFor,
  NgTemplateOutlet,
  AsyncPipe,
  PercentPipe,
} from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormlyRekoleSelectOptionsPipe } from './formly-rekole-select-options.pipe';

@Component({
  selector: 'app-formly-rekole-select',
  template: `
    <ng-template #selectAll let-selectOptions="selectOptions">
      <mat-option (click)="toggleSelectAll(selectOptions)">
        <mat-pseudo-checkbox
          class="mat-option-pseudo-checkbox"
          [state]="_getSelectAllState(selectOptions)"
        >
        </mat-pseudo-checkbox>
        {{ props.selectAllOption }}
      </mat-option>
      -
      <mat-option (click)="toggleSelectAll(selectOptions)">
        <mat-checkbox
          class="mat-option-checkbox"
          [checked]="_getSelectAllState(selectOptions) === 'checked'"
          [indeterminate]="
            _getSelectAllState(selectOptions) === 'indeterminate'
          "
        >
        </mat-checkbox>
        {{ props.selectAllOption }}
      </mat-option>
    </ng-template>
    <mat-select
      panelClass="rekole-select-panel"
      [id]="id"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [placeholder]="props.placeholder ?? ''"
      [tabIndex]="props.tabindex"
      [required]="required"
      [compareWith]="props.compareWith ?? defaultOptions.props.compareWith"
      [multiple]="props.multiple"
      (selectionChange)="change($event)"
      [errorStateMatcher]="errorStateMatcher"
      [disableOptionCentering]="props.disableOptionCentering"
      [typeaheadDebounceInterval]="props.typeaheadDebounceInterval"
    >
      <mat-select-trigger>
        <ng-container
          *ngFor="
            let opt of props.options
              | formlyRekoleSelectOptions: field
              | async as selectOptions
          "
        >
          @if (opt.value === formControl.value) {
            {{ opt.label }}
          }
        </ng-container>
      </mat-select-trigger>

      @if (
        props.options | formlyRekoleSelectOptions: field | async;
        as selectOptions
      ) {
        @if (props.multiple && props.selectAllOption) {
          <ng-container
            [ngTemplateOutlet]="selectAll"
            [ngTemplateOutletContext]="{
              selectOptions: selectOptions,
            }"
          >
          </ng-container>
        }
        @for (item of selectOptions; track item) {
          @if (item.group) {
            <mat-optgroup [label]="item.label">
              @for (child of item.group; track child) {
                <mat-option [value]="child.value" [disabled]="child.disabled">
                  {{ child.label }}
                </mat-option>
              }
            </mat-optgroup>
          }
          @if (!item.group) {
            <mat-option [value]="item.value" [disabled]="item.disabled">
              {{ item.label }}
              @if (item.data?.percentage || item.data?.economicLifetime) {
                <span class="small"
                  ><br />{{ 'SHARED.REKOLE_SELECT.PERCENTAGE' | translate }}
                  {{ item.data?.percentage | percent }},
                  {{ 'SHARED.REKOLE_SELECT.ECONOMIC_LIFETIME' | translate }}
                  {{ item.data?.economicLifetime }}
                  {{ 'SHARED.UNITS.TIME.YEAR.PLURAL' | translate }}</span
                >
              }
            </mat-option>
          }
        }
      }
    </mat-select>
  `,
  styleUrls: ['./formly-rekole-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatOption,
    MatCheckbox,
    MatSelect,
    ReactiveFormsModule,
    FormlyModule,
    MatSelectTrigger,
    NgFor,
    NgTemplateOutlet,
    MatOptgroup,
    AsyncPipe,
    PercentPipe,
    TranslateModule,
    FormlyRekoleSelectOptionsPipe,
    MatPseudoCheckbox,
  ],
})
export class FormlyRekoleSelectComponent extends FormlyFieldSelect {
  override defaultOptions = {
    props: {
      compareWith(o1: any, o2: any) {
        return o1 === o2;
      },
    },
  };

  _getSelectAllState(options: any): MatPseudoCheckboxState {
    const tmp: any = super.getSelectAllState(options);
    if (tmp === '') {
      return 'unchecked';
    } else {
      return tmp;
    }
  }
}
